'use strict';

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.normalizeTimeString = normalizeTimeString;
exports.processOperatingHours = processOperatingHours;

var _lodash = require('lodash');

var _momentTimezone = require('moment-timezone');

var _momentTimezone2 = _interopRequireDefault(_momentTimezone);

var _timeUtils = require('./timeUtils');

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

function _toConsumableArray(arr) { if (Array.isArray(arr)) { for (var i = 0, arr2 = Array(arr.length); i < arr.length; i++) { arr2[i] = arr[i]; } return arr2; } else { return Array.from(arr); } }

/**
 * Clean up any random extra whitespace in the given time string (expected to
 * be roughly in a format like " 5:00AM") for the service provider operating
 * hours.
 */
function normalizeTimeString(str) {
  return str.replace(/\s/g, '') // remove all whitespace
  .replace('AM', ' AM').replace('PM', ' PM');
}

/**
 * Determine if the service provider (which is located in the given `timeZone`) is
 * currently open, based on the open/close hours of a given day of the week.
 */
function evalOpenStatus(targetDay, openingTime, closingTime, timeZone) {
  var timeNowAtProvider = (0, _timeUtils.getCurrentTime)(timeZone);
  var targetDayAtProvider = (0, _momentTimezone2.default)(timeNowAtProvider).day(targetDay);

  var dayNameAtProvider = targetDayAtProvider.format('dddd');

  // fail fast if `targetDay` is not the current day of the week at the provider
  if (dayNameAtProvider !== targetDay) return false;

  // create time objects for opening/closing times of the target day in the provider's time-zone:
  var ymd = targetDayAtProvider.format('YYYY-MM-DD');
  var parseFormat = 'YYYY-MM-DD h:mm A';
  var targetDayOpenAt = _momentTimezone2.default.tz(ymd + ' ' + openingTime, parseFormat, timeZone);
  var targetDayCloseAt = _momentTimezone2.default.tz(ymd + ' ' + closingTime, parseFormat, timeZone);

  // If targetDayOpenAt and targetDayCloseAt are same,
  // it is supposed that targetDayCloseAt is one day more.
  if (targetDayOpenAt.isSame(targetDayCloseAt)) targetDayCloseAt.add(1, 'day');
  // determine if the service provider is open right now, due to the current time
  // in the provider's time-zone falling between the open and close times for
  // the day of the week we're currently processing:
  return timeNowAtProvider.isBetween(targetDayOpenAt, targetDayCloseAt);
}

/**
 * This function serves two purposes:
 *   1. Process the incoming array of service provider operating hours to collapse multiple
 *      days having the same open/close hours into single entries.
 *   2. Determine if the service provider is currently open, based on the specified days
 *      and opening/closing hours.
 * It returns an object with two properties:
 *    * `hours`: the list of processed day-ranges with open/close hours
 *    * `isOpen`: a boolean indicating whether the provider is *currently* open
 */
function processOperatingHours(operatingHours, timeZone) {
  // incoming operatingHours will look something like this:
  // [  {:day=>"Monday", :open=>" 5:00AM", :close=>" 5:00PM"},
  //    {:day=>"Tuesday", :open=>" 5:00AM", :close=>" 5:00PM"},
  //    {:day=>"Wednesday", :open=>" 5:00AM", :close=>" 5:00PM"},
  //    {:day=>"Thursday", :open=>" 5:00AM", :close=>" 5:00PM"},
  //    {:day=>"Friday", :open=>" 5:00AM", :close=>" 5:00PM"},
  //    {:day=>"Saturday", :open=>" 5:00AM", :close=>" 3:00PM"},
  //    {:day=>"Sunday", :open=>" 5:00AM", :close=>" 3:00PM"} ]
  //
  // output should be...
  // [  { start: 'Monday', end: 'Friday', open: '5 AM', close: '5 PM' },
  //    { start: 'Saturday', end: 'Sunday', open: '5 AM', close: '3 PM' } ]

  var isOpen = false;

  var processedHours = operatingHours.reduce(function (processed, dayInfo) {
    // get the last processed day, and all the other processed days before that one:
    var lastDay = processed.length ? (0, _lodash.last)(processed) : { start: '', end: '', open: '', close: '' };
    var otherDays = (0, _lodash.initial)(processed);

    // this will be the full English name of the day of the week we're processing now:
    var targetDay = dayInfo.day;

    // clean up the open/close times:
    var open = normalizeTimeString(dayInfo.open);
    var close = normalizeTimeString(dayInfo.close);

    // if we haven't already found that the provider is open on a previous
    // day, check whether they're open on this day:
    isOpen = isOpen || evalOpenStatus(targetDay, open, close, timeZone);

    // compare this day's open/close with the last day's open/close:
    if (open === lastDay.open && close === lastDay.close) {
      // same open/close hours so merge this day into the last day
      return [].concat(_toConsumableArray(otherDays), [{
        start: lastDay.start || targetDay,
        end: targetDay,
        open: open,
        close: close
      }]);
    }
    // create a new day since this one has different hours from the last one
    return [].concat(_toConsumableArray(processed), [{
      start: targetDay,
      end: targetDay,
      open: open,
      close: close
    }]);
  }, []);

  return { hours: processedHours, isOpen: isOpen };
}


//////////////////
// WEBPACK FOOTER
// ./node_modules/decisiv-domain-utils/build/processOpenStatus.js
// module id = 1489
// module chunks = 2